/**
 * AdminPanel — full platform administration dashboard.
 *
 * Modal panel listing all Clerk users with platform stats. Admin can:
 *   • Adjust credits (delta — recorded in usage_ledger)
 *   • Enable/disable users (Clerk ban/unban)
 *   • Inspect execution history
 *   • Inspect payment history (MP/Stripe)
 *   • [placeholder] View user assets
 *
 * Visibility is controlled by the parent (TopNavBar) via /admin/me/check.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  X, Search, Loader2, Shield, Users, CreditCard, Activity, FileImage,
  Receipt, RefreshCw, CheckCircle2, XCircle, Ban, ShieldCheck,
  Plus, Minus, Clock, ChevronRight, AlertTriangle, Mail,
  CheckSquare, Square, Download, FileSpreadsheet,
  LayoutDashboard, TrendingUp, DollarSign, UserPlus, Zap, Film, ShieldAlert, Plug,
  Sparkles, Bot, GraduationCap, MessageSquareWarning, HardDriveUpload,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';
import AdminDirectorsPanel from './AdminDirectorsPanel';
import AdminMcpAccessPanel from './AdminMcpAccessPanel';
import AdminNodeHealthPanel from './AdminNodeHealthPanel';
import AdminSecurityPanel from './AdminSecurityPanel';
import AdminBackgroundPanel from './AdminBackgroundPanel';
import AdminLmxPanel from './AdminLmxPanel';
import AdminCostsPanel from './AdminCostsPanel';
import AdminLlmPanel from './AdminLlmPanel';
import AdminLearningPanel from './AdminLearningPanel';
import AdminFeedbackPanel from './AdminFeedbackPanel';
import AdminUploadsPanel from './AdminUploadsPanel';
import UserUploadPlanTab from './UserUploadPlanTab';

/**
 * Shell for the "Seguridad y Status" screen. Feature A (node health) and
 * Feature B (security events) stay in separate components/endpoints.
 */
function SecurityStatusTab() {
  const [sub, setSub] = useState('nodes');
  return (
    <div className="admin-securestatus" data-testid="admin-security-status-tab">
      <div className="admin-tabs">
        <button
          className={sub === 'nodes' ? 'active' : ''}
          onClick={() => setSub('nodes')}
          data-testid="admin-subtab-node-health"
        >
          <Activity size={13} /> Salud de nodos
        </button>
        <button
          className={sub === 'security' ? 'active' : ''}
          onClick={() => setSub('security')}
          data-testid="admin-subtab-security-events"
        >
          <ShieldAlert size={13} /> Intentos y picos
        </button>
        <button
          className={sub === 'mcp' ? 'active' : ''}
          onClick={() => setSub('mcp')}
          data-testid="admin-subtab-mcp-access"
        >
          <Plug size={13} /> Acceso MCP
        </button>
      </div>
      <div className="admin-tab-body">
        {sub === 'nodes' && <AdminNodeHealthPanel />}
        {sub === 'security' && <AdminSecurityPanel />}
        {sub === 'mcp' && <AdminMcpAccessPanel />}
      </div>
    </div>
  );
}

const fmtDate = (msOrIso) => {
  if (!msOrIso) return '—';
  const d = typeof msOrIso === 'number' ? new Date(msOrIso) : new Date(msOrIso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const fmtSeconds = (s) => {
  s = Number(s) || 0;
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const fmtMoney = (amount, currency = 'USD') => {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency', currency, maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
};

const fmtBytes = (b) => {
  b = Number(b) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

// CSV cell escaper (handles commas, quotes, newlines)
const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows, filename) => {
  const csv = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
};

export default function AdminPanel({ onClose }) {
  const [mainTab, setMainTab] = useState('overview');   // 'overview' | 'users'
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDelta, setBulkDelta] = useState(10);
  const [bulkNote, setBulkNote] = useState('');

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminService.listUsers({ search: debounced, limit: 100 });
      setUsers(r.users || []);
      setTotal(r.total || 0);
      // Drop selections that no longer exist in the new list
      setSelectedIds((prev) => {
        const next = new Set();
        const ids = new Set((r.users || []).map((u) => u.user_id));
        prev.forEach((id) => { if (ids.has(id)) next.add(id); });
        return next;
      });
    } catch (err) {
      toast.error('Error al cargar usuarios');
      console.error(err);
    } finally { setLoading(false); }
  }, [debounced]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const allSelected = users.length > 0 && selectedIds.size === users.length;
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(users.map((u) => u.user_id)));
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyBulk = async (sign) => {
    const value = sign * Math.abs(parseInt(bulkDelta, 10) || 0);
    if (!value) { toast.error('Ingresá un monto'); return; }
    if (selectedIds.size === 0) { toast.error('Sin usuarios seleccionados'); return; }
    if (!window.confirm(
      `${value > 0 ? 'Otorgar' : 'Restar'} ${Math.abs(value)} créditos a ${selectedIds.size} usuario(s)?`
    )) return;
    setBulkBusy(true);
    try {
      const r = await adminService.bulkAdjustCredits({
        userIds: Array.from(selectedIds), delta: value, note: bulkNote,
      });
      toast.success(`Aplicado a ${r.ok_count}/${selectedIds.size}${r.error_count ? ` · ${r.error_count} con error` : ''}`);
      setBulkNote('');
      setSelectedIds(new Set());
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la operación masiva');
    } finally { setBulkBusy(false); }
  };

  const exportCsv = () => {
    const rows = [
      ['user_id', 'email', 'nombre', 'rol', 'deshabilitado',
       'online', 'ultimo_ingreso', 'creditos_balance', 'creditos_gastados',
       'ejecuciones', 'segundos_uso'],
    ];
    const source = selectedIds.size
      ? users.filter((u) => selectedIds.has(u.user_id))
      : users;
    source.forEach((u) => {
      rows.push([
        u.user_id, u.email || '', u.name || '',
        u.role, u.banned ? 'sí' : 'no',
        u.online ? 'sí' : 'no',
        u.last_sign_in_at ? new Date(u.last_sign_in_at).toISOString() : '',
        u.stats.credits_balance, u.stats.credits_spent,
        u.stats.execution_count, Math.round(u.stats.execution_seconds || 0),
      ]);
    });
    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    downloadCsv(rows, `medialabs-users-${ts}.csv`);
    toast.success(`CSV exportado · ${rows.length - 1} filas`);
  };

  return (
    <div className="admin-overlay" data-testid="admin-panel-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} data-testid="admin-panel">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-header-title">
            <Shield size={20} />
            <h2>Administración</h2>
            <span className="admin-header-count" data-testid="admin-total-users">
              {total} usuarios
            </span>
          </div>
          <div className="admin-main-tabs">
            <button
              className={mainTab === 'overview' ? 'active' : ''}
              onClick={() => setMainTab('overview')}
              data-testid="admin-main-tab-overview"
            >
              <LayoutDashboard size={13} /> Resumen
            </button>
            <button
              className={mainTab === 'users' ? 'active' : ''}
              onClick={() => setMainTab('users')}
              data-testid="admin-main-tab-users"
            >
              <Users size={13} /> Usuarios
            </button>
            <button
              className={mainTab === 'directors' ? 'active' : ''}
              onClick={() => setMainTab('directors')}
              data-testid="admin-main-tab-directors"
            >
              <Film size={13} /> Directors
            </button>
            <button
              className={mainTab === 'health' ? 'active' : ''}
              onClick={() => setMainTab('health')}
              data-testid="admin-main-tab-health"
            >
              <ShieldAlert size={13} /> Seguridad y Status
            </button>
            <button
              className={mainTab === 'costs' ? 'active' : ''}
              onClick={() => setMainTab('costs')}
              data-testid="admin-main-tab-costs"
            >
              <DollarSign size={13} /> Costos y margen
            </button>
            <button
              className={mainTab === 'llm' ? 'active' : ''}
              onClick={() => setMainTab('llm')}
              data-testid="admin-main-tab-llm"
            >
              <Bot size={13} /> Motor de IA
            </button>
            <button
              className={mainTab === 'background' ? 'active' : ''}
              onClick={() => setMainTab('background')}
              data-testid="admin-main-tab-background"
            >
              <FileImage size={13} /> Fondo
            </button>
            <button
              className={mainTab === 'lmx' ? 'active' : ''}
              onClick={() => setMainTab('lmx')}
              data-testid="admin-main-tab-lmx"
            >
              <Sparkles size={13} /> Lumière (LMX)
            </button>
            <button
              className={mainTab === 'learning' ? 'active' : ''}
              onClick={() => setMainTab('learning')}
              data-testid="admin-main-tab-learning"
            >
              <GraduationCap size={13} /> Capítulos
            </button>
            <button
              className={mainTab === 'feedback' ? 'active' : ''}
              onClick={() => setMainTab('feedback')}
              data-testid="admin-main-tab-feedback"
            >
              <MessageSquareWarning size={13} /> Reportes
            </button>
            <button
              className={mainTab === 'uploads' ? 'active' : ''}
              onClick={() => setMainTab('uploads')}
              data-testid="admin-main-tab-uploads"
            >
              <HardDriveUpload size={13} /> Subidas
            </button>
          </div>
          <button className="admin-close" onClick={onClose} data-testid="admin-close">
            <X size={20} />
          </button>
        </div>

        {/* AP-12 · toda pestaña desplaza en vertical y, si hace falta, en horizontal */}
        <div className="admin-tab-scroll" data-testid="admin-tab-scroll" key={mainTab}>
        {mainTab === 'overview' ? (
          <OverviewTab />
        ) : mainTab === 'directors' ? (
          <AdminDirectorsPanel />
        ) : mainTab === 'health' ? (
          <SecurityStatusTab />
        ) : mainTab === 'costs' ? (
          <AdminCostsPanel />
        ) : mainTab === 'llm' ? (
          <AdminLlmPanel />
        ) : mainTab === 'background' ? (
          <AdminBackgroundPanel />
        ) : mainTab === 'lmx' ? (
          <AdminLmxPanel />
        ) : mainTab === 'learning' ? (
          <AdminLearningPanel />
        ) : mainTab === 'feedback' ? (
          <AdminFeedbackPanel />
        ) : mainTab === 'uploads' ? (
          <AdminUploadsPanel />
        ) : (
          <>
            {/* Toolbar */}
            <div className="admin-toolbar">
          <button
            className="admin-checkall"
            onClick={toggleAll}
            title={allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
            data-testid="admin-checkall"
          >
            {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
          </button>
          <div className="admin-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Buscar por email, nombre o ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search"
            />
          </div>
          <button
            className="admin-csv-btn"
            onClick={exportCsv}
            title={`Exportar ${selectedIds.size || users.length} fila(s) a CSV`}
            data-testid="admin-csv-export"
          >
            <FileSpreadsheet size={14} />
            CSV
          </button>
          <button className="admin-refresh" onClick={fetchUsers} disabled={loading}>
            {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
          </button>
        </div>

        {/* Bulk action bar (visible only when ≥1 selected) */}
        {selectedIds.size > 0 && (
          <div className="admin-bulkbar" data-testid="admin-bulkbar">
            <span className="admin-bulk-count">{selectedIds.size} seleccionado(s)</span>
            <input
              type="number"
              value={bulkDelta}
              onChange={(e) => setBulkDelta(e.target.value)}
              min={1}
              className="admin-credits-input admin-bulk-input"
              data-testid="admin-bulk-delta"
            />
            <input
              type="text"
              placeholder="Motivo (opcional)…"
              value={bulkNote}
              onChange={(e) => setBulkNote(e.target.value)}
              className="admin-credits-note"
              data-testid="admin-bulk-note"
            />
            <button
              className="admin-btn admin-btn-grant"
              onClick={() => applyBulk(+1)}
              disabled={bulkBusy}
              data-testid="admin-bulk-grant"
            >
              {bulkBusy ? <Loader2 size={12} className="spin" /> : <Plus size={12} />}
              Otorgar
            </button>
            <button
              className="admin-btn admin-btn-deduct"
              onClick={() => applyBulk(-1)}
              disabled={bulkBusy}
              data-testid="admin-bulk-deduct"
            >
              {bulkBusy ? <Loader2 size={12} className="spin" /> : <Minus size={12} />}
              Restar
            </button>
            <button
              className="admin-bulk-cancel"
              onClick={() => setSelectedIds(new Set())}
              title="Limpiar selección"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* Body: split panel */}
        <div className="admin-body">
          <UsersTable
            users={users}
            loading={loading}
            selectedId={selectedUser?.user_id}
            selectedIds={selectedIds}
            onToggleOne={toggleOne}
            onSelect={setSelectedUser}
          />
          {selectedUser ? (
            <UserDetail
              user={selectedUser}
              onChange={(patch) => setSelectedUser({ ...selectedUser, ...patch })}
              onRefreshList={fetchUsers}
              onClose={() => setSelectedUser(null)}
            />
          ) : (
            <div className="admin-detail-empty">
              <Users size={32} />
              <span>Seleccioná un usuario</span>
              <small>para ver créditos, ejecuciones y pagos</small>
            </div>
          )}
        </div>
        </>
        )}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
function UsersTable({ users, loading, selectedId, selectedIds, onToggleOne, onSelect }) {
  if (loading && !users.length) {
    return (
      <div className="admin-users-table">
        <div className="admin-empty"><Loader2 size={20} className="spin" /> Cargando…</div>
      </div>
    );
  }
  if (!users.length) {
    return (
      <div className="admin-users-table">
        <div className="admin-empty"><Users size={28} /> Sin resultados</div>
      </div>
    );
  }
  return (
    <div className="admin-users-table" data-testid="admin-users-table">
      {users.map((u) => {
        const isChecked = selectedIds.has(u.user_id);
        return (
          <div
            key={u.user_id}
            className={`admin-user-row ${selectedId === u.user_id ? 'active' : ''} ${u.banned ? 'banned' : ''} ${isChecked ? 'checked' : ''}`}
            data-testid={`admin-user-row-${u.user_id}`}
          >
            <button
              className="admin-user-check"
              onClick={(e) => { e.stopPropagation(); onToggleOne(u.user_id); }}
              title={isChecked ? 'Deseleccionar' : 'Seleccionar'}
              data-testid={`admin-user-check-${u.user_id}`}
            >
              {isChecked ? <CheckSquare size={14} /> : <Square size={14} />}
            </button>
            <button className="admin-user-body" onClick={() => onSelect(u)}>
              <div className="admin-user-avatar">
                {u.image_url ? (
                  <img src={u.image_url} alt={u.name || u.email} />
                ) : (
                  <span>{(u.name || u.email || 'U').slice(0, 1).toUpperCase()}</span>
                )}
                <span
                  className={`admin-user-dot ${u.online ? 'online' : 'offline'}`}
                  title={u.online ? 'Conectado ahora' : 'Desconectado'}
                />
              </div>
              <div className="admin-user-main">
                <div className="admin-user-line1">
                  <span className="admin-user-name">{u.name || '—'}</span>
                  {u.role !== 'user' && (
                    <span className={`admin-role-badge role-${u.role}`}>{u.role}</span>
                  )}
                  {u.banned && <span className="admin-role-badge role-banned">deshabilitado</span>}
                </div>
                <div className="admin-user-line2">
                  <Mail size={10} /> {u.email || u.user_id.slice(0, 16) + '…'}
                </div>
                <div className="admin-user-line3">
                  <span>último ingreso: {fmtDate(u.last_sign_in_at)}</span>
                </div>
              </div>
              <div className="admin-user-stats">
                <span className="admin-stat-credits">{u.stats.credits_balance} cr</span>
                <span className="admin-stat-execs">{u.stats.execution_count} ejec.</span>
              </div>
              <ChevronRight size={14} className="admin-user-chev" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
function UserDetail({ user, onChange, onRefreshList, onClose }) {
  const [tab, setTab] = useState('credits');

  return (
    <div className="admin-detail" data-testid="admin-user-detail">
      <div className="admin-detail-header">
        <div className="admin-detail-user">
          {user.image_url
            ? <img src={user.image_url} alt="" className="admin-detail-avatar" />
            : <div className="admin-detail-avatar placeholder">{(user.name || 'U').slice(0, 1)}</div>}
          <div>
            <div className="admin-detail-name">{user.name || user.email || user.user_id}</div>
            <div className="admin-detail-meta">
              {user.email && <span><Mail size={11} /> {user.email}</span>}
              <span className={`admin-online-pill ${user.online ? 'online' : 'offline'}`}>
                {user.online ? 'En línea' : 'Desconectado'}
              </span>
              {user.banned && <span className="admin-online-pill banned">deshabilitado</span>}
            </div>
          </div>
        </div>
        <button className="admin-detail-close" onClick={onClose}><X size={16} /></button>
      </div>

      <div className="admin-detail-quickstats">
        <div><CreditCard size={12} /> {user.stats.credits_balance} cr balance</div>
        <div><Activity size={12} /> {user.stats.execution_count} ejecuciones</div>
        <div><Clock size={12} /> {fmtSeconds(user.stats.execution_seconds)} de uso</div>
        <div>último ingreso · {fmtDate(user.last_sign_in_at)}</div>
      </div>

      <div className="admin-tabs">
        <button className={tab === 'credits' ? 'active' : ''} onClick={() => setTab('credits')} data-testid="admin-tab-credits">
          <CreditCard size={12} /> Créditos
        </button>
        <button className={tab === 'execs' ? 'active' : ''} onClick={() => setTab('execs')} data-testid="admin-tab-execs">
          <Activity size={12} /> Ejecuciones
        </button>
        <button className={tab === 'payments' ? 'active' : ''} onClick={() => setTab('payments')} data-testid="admin-tab-payments">
          <Receipt size={12} /> Pagos
        </button>
        <button className={tab === 'assets' ? 'active' : ''} onClick={() => setTab('assets')} data-testid="admin-tab-assets">
          <FileImage size={12} /> Assets
        </button>
        <button className={tab === 'status' ? 'active' : ''} onClick={() => setTab('status')} data-testid="admin-tab-status">
          <ShieldCheck size={12} /> Estado
        </button>
        <button className={tab === 'plan' ? 'active' : ''} onClick={() => setTab('plan')} data-testid="admin-tab-plan">
          <HardDriveUpload size={12} /> Plan
        </button>
      </div>

      <div className="admin-tab-body">
        {tab === 'credits' && <CreditsTab user={user} onChange={onChange} onRefreshList={onRefreshList} />}
        {tab === 'execs' && <ExecutionsTab userId={user.user_id} />}
        {tab === 'payments' && <PaymentsTab userId={user.user_id} />}
        {tab === 'assets' && <AssetsTab user={user} />}
        {tab === 'status' && <StatusTab user={user} onChange={onChange} onRefreshList={onRefreshList} />}
        {tab === 'plan' && <UserUploadPlanTab user={user} />}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
function CreditsTab({ user, onChange, onRefreshList }) {
  const [delta, setDelta] = useState(10);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const apply = async (sign) => {
    const value = sign * Math.abs(parseInt(delta, 10) || 0);
    if (!value) { toast.error('Ingresá un monto'); return; }
    setBusy(true);
    try {
      const r = await adminService.adjustCredits(user.user_id, value, note);
      toast.success(`Balance actualizado: ${r.credits_balance} cr`);
      onChange({ stats: { ...user.stats, credits_balance: r.credits_balance } });
      onRefreshList();
      setNote('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ajustar créditos');
    } finally { setBusy(false); }
  };

  return (
    <div className="admin-credits-tab">
      <div className="admin-credits-current">
        <span>Balance actual</span>
        <strong data-testid="admin-credits-current">{user.stats.credits_balance}</strong>
        <span className="admin-credits-spent">· Gastado: {user.stats.credits_spent} cr</span>
      </div>

      <div className="admin-credits-controls">
        <input
          type="number"
          value={delta}
          onChange={(e) => setDelta(e.target.value)}
          min={1}
          className="admin-credits-input"
          data-testid="admin-credits-input"
        />
        <input
          type="text"
          placeholder="Motivo (opcional)…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="admin-credits-note"
          data-testid="admin-credits-note"
        />
        <button
          className="admin-btn admin-btn-grant"
          onClick={() => apply(+1)}
          disabled={busy}
          data-testid="admin-credits-grant"
        >
          {busy ? <Loader2 size={12} className="spin" /> : <Plus size={12} />}
          Sumar
        </button>
        <button
          className="admin-btn admin-btn-deduct"
          onClick={() => apply(-1)}
          disabled={busy}
          data-testid="admin-credits-deduct"
        >
          {busy ? <Loader2 size={12} className="spin" /> : <Minus size={12} />}
          Restar
        </button>
      </div>

      <p className="admin-tab-help">
        <AlertTriangle size={11} /> Cada ajuste queda registrado en <code>usage_ledger</code> con
        razón <code>admin_adjustment</code>.
      </p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
function ExecutionsTab({ userId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    adminService.listExecutions(userId, { limit: 100 })
      .then((r) => { if (!cancel) setRows(r.executions || []); })
      .catch(() => toast.error('No se pudo cargar el historial'))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [userId]);

  const totalSec = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.duration_seconds) || 0), 0),
    [rows]
  );

  if (loading) return <div className="admin-empty"><Loader2 size={20} className="spin" /></div>;
  if (!rows.length) return <div className="admin-empty">Sin ejecuciones</div>;

  return (
    <div className="admin-execs-tab">
      <div className="admin-execs-header">
        <span>{rows.length} ejecuciones</span>
        <span>Tiempo total: <strong>{fmtSeconds(totalSec)}</strong></span>
      </div>
      <div className="admin-execs-list" data-testid="admin-execs-list">
        {rows.map((r) => (
          <div key={r.id} className={`admin-exec-row status-${r.status}`}>
            <span className="admin-exec-type">{r.result_type}</span>
            <span className="admin-exec-status">
              {r.status === 'completed' && <CheckCircle2 size={11} />}
              {r.status === 'failed' && <XCircle size={11} />}
              {r.status === 'processing' && <Loader2 size={11} className="spin" />}
              {r.status}
            </span>
            <span className="admin-exec-credits">{r.credits_used || 0} cr</span>
            <span className="admin-exec-time">{fmtSeconds(r.duration_seconds || 0)}</span>
            <span className="admin-exec-date">{fmtDate(r.created_at)}</span>
            {r.error_message && (
              <span className="admin-exec-err" title={r.error_message}>
                {String(r.error_message).slice(0, 80)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
function PaymentsTab({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    adminService.listPayments(userId, { limit: 100 })
      .then((r) => { if (!cancel) setData(r); })
      .catch(() => toast.error('No se pudo cargar pagos'))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [userId]);

  if (loading) return <div className="admin-empty"><Loader2 size={20} className="spin" /></div>;
  const txs = data?.transactions || [];
  const summary = data?.summary || {};
  if (!txs.length) return <div className="admin-empty">Sin pagos registrados</div>;

  return (
    <div className="admin-payments-tab">
      <div className="admin-payments-summary" data-testid="admin-payments-summary">
        <div><strong>{summary.total_count}</strong><span>pagos</span></div>
        <div><strong>{summary.completed_count}</strong><span>completados</span></div>
        <div className={summary.failed_count ? 'has-fail' : ''}>
          <strong>{summary.failed_count}</strong><span>fallidos</span>
        </div>
        <div><strong>{summary.total_credits_purchased}</strong><span>créditos comprados</span></div>
      </div>

      <div className="admin-payments-list" data-testid="admin-payments-list">
        {txs.map((t) => (
          <div key={t.id} className={`admin-payment-row status-${t.status}`}>
            <span className="admin-payment-status">
              {t.status === 'completed' && <CheckCircle2 size={11} />}
              {(t.status === 'failed' || t.status === 'refunded') && <XCircle size={11} />}
              {t.status === 'initiated' && <Loader2 size={11} className="spin" />}
              {t.status}
            </span>
            <span className="admin-payment-pkg">{t.package_id || '—'}</span>
            <span className="admin-payment-amount">
              {fmtMoney((Number(t.amount_cents) || 0) / 100, (t.currency || 'USD').toUpperCase())}
            </span>
            <span className="admin-payment-credits">{t.credits || 0} cr</span>
            <span className="admin-payment-prov">{t.provider}</span>
            <span className="admin-payment-date">{fmtDate(t.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
function AssetsTab({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');  // '' | 'image' | 'video' | 'audio'

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    adminService.listAssets(user.user_id, { assetType: typeFilter || null, limit: 60 })
      .then((r) => { if (!cancel) setData(r); })
      .catch(() => toast.error('No se pudieron cargar los assets'))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user.user_id, typeFilter]);

  if (loading && !data) return <div className="admin-empty"><Loader2 size={20} className="spin" /></div>;
  const assets = data?.assets || [];
  const summary = data?.summary || {};

  return (
    <div className="admin-assets-tab">
      <div className="admin-assets-header">
        <div className="admin-assets-counts" data-testid="admin-assets-counts">
          <span>{summary.shown || 0} mostrados</span>
          {Object.entries(summary.by_type || {}).map(([t, n]) => (
            <span key={t} className={`admin-assets-type-pill type-${t}`}>{n} {t}</span>
          ))}
          <span>· {fmtBytes(summary.total_size_bytes || 0)}</span>
        </div>
        <div className="admin-assets-filters">
          <button
            className={typeFilter === '' ? 'active' : ''}
            onClick={() => setTypeFilter('')}
            data-testid="admin-assets-filter-all"
          >Todos</button>
          <button
            className={typeFilter === 'image' ? 'active' : ''}
            onClick={() => setTypeFilter('image')}
            data-testid="admin-assets-filter-image"
          ><FileImage size={11} /> Imágenes</button>
          <button
            className={typeFilter === 'video' ? 'active' : ''}
            onClick={() => setTypeFilter('video')}
            data-testid="admin-assets-filter-video"
          >Videos</button>
          <button
            className={typeFilter === 'audio' ? 'active' : ''}
            onClick={() => setTypeFilter('audio')}
            data-testid="admin-assets-filter-audio"
          >Audios</button>
        </div>
      </div>

      {!assets.length ? (
        <div className="admin-empty"><FileImage size={28} /> Sin assets</div>
      ) : (
        <div className="admin-assets-grid" data-testid="admin-assets-grid">
          {assets.map((a) => (
            <AssetCard key={a.id} asset={a} />
          ))}
        </div>
      )}
    </div>
  );
}

function AssetCard({ asset }) {
  const thumb = asset.metadata?.thumbnail_url;
  const isImage = asset.asset_type === 'image';
  const isVideo = asset.asset_type === 'video';
  const isAudio = asset.asset_type === 'audio';
  return (
    <a
      href={asset.url}
      target="_blank"
      rel="noreferrer"
      className={`admin-asset-card type-${asset.asset_type}`}
      title={asset.filename || asset.id}
      data-testid={`admin-asset-card-${asset.id}`}
    >
      <div className="admin-asset-thumb">
        {isImage ? (
          <img src={asset.url} alt="" loading="lazy" />
        ) : isVideo ? (
          thumb
            ? <img src={thumb} alt="" loading="lazy" />
            : <div className="admin-asset-icon"><FileImage size={18} /></div>
        ) : isAudio ? (
          <div className="admin-asset-icon audio"><Activity size={18} /></div>
        ) : (
          <div className="admin-asset-icon"><FileImage size={18} /></div>
        )}
        <span className={`admin-asset-type-badge type-${asset.asset_type}`}>
          {asset.asset_type}
        </span>
      </div>
      <div className="admin-asset-meta">
        <div className="admin-asset-name">{asset.filename || asset.id.slice(0, 10)}</div>
        <div className="admin-asset-sub">
          {fmtBytes(asset.file_size_bytes)} · {fmtDate(asset.created_at)}
        </div>
      </div>
    </a>
  );
}

// ───────────────────────────────────────────────────────────────────
function StatusTab({ user, onChange, onRefreshList }) {
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (!window.confirm(
      user.banned
        ? `¿Habilitar a ${user.email || user.user_id}? Va a poder iniciar sesión nuevamente.`
        : `¿Deshabilitar a ${user.email || user.user_id}? No va a poder iniciar sesión.`
    )) return;
    setBusy(true);
    try {
      const r = await adminService.setUserStatus(user.user_id, !!user.banned);
      onChange({ banned: !r.enabled });
      onRefreshList();
      toast.success(r.enabled ? 'Usuario habilitado' : 'Usuario deshabilitado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar estado');
    } finally { setBusy(false); }
  };

  return (
    <div className="admin-status-tab">
      <p>
        Estado actual:{' '}
        <strong className={user.banned ? 'is-banned' : 'is-enabled'}>
          {user.banned ? 'Deshabilitado' : 'Habilitado'}
        </strong>
      </p>
      <button
        className={`admin-btn ${user.banned ? 'admin-btn-grant' : 'admin-btn-deduct'}`}
        onClick={toggle}
        disabled={busy}
        data-testid="admin-status-toggle"
      >
        {busy ? <Loader2 size={12} className="spin" /> : user.banned ? <ShieldCheck size={12} /> : <Ban size={12} />}
        {user.banned ? 'Habilitar usuario' : 'Deshabilitar usuario'}
      </button>
      <p className="admin-tab-help">
        Esta acción se sincroniza con Clerk inmediatamente — la sesión activa del usuario será revocada.
      </p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Overview dashboard — KPI cards + sparkline + top consumers
// ───────────────────────────────────────────────────────────────────
function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminService.getMetrics()
      .then(setData)
      .catch(() => toast.error('No se pudieron cargar las métricas'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="admin-empty"><Loader2 size={28} className="spin" /> Cargando métricas…</div>;
  }
  if (!data) return null;

  const kpis = [
    {
      label: 'Usuarios totales',
      value: data.users.total,
      sub: `+${data.users.new_this_week} esta semana`,
      icon: <Users size={16} />,
      accent: 'indigo',
    },
    {
      label: 'Conectados ahora',
      value: data.users.active_now,
      sub: 'últimos 5 min',
      icon: <UserPlus size={16} />,
      accent: 'green',
    },
    {
      label: 'Créditos hoy',
      value: data.credits.spent_today,
      sub: `${data.credits.spent_this_week} esta semana`,
      icon: <Zap size={16} />,
      accent: 'amber',
    },
    {
      label: 'Créditos totales',
      value: data.credits.spent_all_time,
      sub: 'histórico',
      icon: <TrendingUp size={16} />,
      accent: 'purple',
    },
    {
      label: 'Pagos últ. 30d',
      value: `$${data.payments.mrr_estimate_last_30d.toFixed(2)}`,
      sub: `${data.payments.count_last_30d} transacciones`,
      icon: <DollarSign size={16} />,
      accent: 'emerald',
    },
  ];

  return (
    <div className="admin-overview" data-testid="admin-overview">
      <div className="admin-overview-header">
        <h3>Métricas de plataforma</h3>
        <button className="admin-refresh" onClick={load} disabled={loading}>
          {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
        </button>
      </div>

      <div className="admin-kpis" data-testid="admin-kpis">
        {kpis.map((k) => (
          <div key={k.label} className={`admin-kpi accent-${k.accent}`}>
            <div className="admin-kpi-icon">{k.icon}</div>
            <div className="admin-kpi-body">
              <span className="admin-kpi-label">{k.label}</span>
              <strong className="admin-kpi-value">{k.value}</strong>
              <span className="admin-kpi-sub">{k.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-overview-row">
        <div className="admin-sparkline-card" data-testid="admin-sparkline-card">
          <div className="admin-sparkline-head">
            <Activity size={14} />
            <span>Ejecuciones últimos 14 días</span>
            <span className="admin-sparkline-total">
              {data.sparkline_executions_14d.reduce((a, d) => a + d.count, 0)} total
            </span>
          </div>
          <Sparkline data={data.sparkline_executions_14d} />
        </div>

        <div className="admin-top-card" data-testid="admin-top-card">
          <div className="admin-top-head">
            <TrendingUp size={14} />
            <span>Top 10 por consumo</span>
          </div>
          {data.top_users_by_consumption.length === 0 ? (
            <div className="admin-empty">Sin datos</div>
          ) : (
            <ol className="admin-top-list">
              {data.top_users_by_consumption.map((u, i) => (
                <li key={u.user_id} className="admin-top-row">
                  <span className="admin-top-rank">#{i + 1}</span>
                  <div className="admin-top-avatar">
                    {u.image_url
                      ? <img src={u.image_url} alt="" />
                      : <span>{(u.name || u.email || 'U').slice(0, 1).toUpperCase()}</span>}
                  </div>
                  <div className="admin-top-main">
                    <div className="admin-top-name">{u.name || u.email || u.user_id.slice(0, 18) + '…'}</div>
                    {u.email && u.name && <div className="admin-top-email">{u.email}</div>}
                  </div>
                  <span className="admin-top-credits">{u.credits_spent} cr</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

// Lightweight CSS-less SVG sparkline (no external libs)
function Sparkline({ data }) {
  const counts = data.map((d) => d.count);
  const max = Math.max(1, ...counts);
  const W = 540;
  const H = 80;
  const pad = 4;
  const step = data.length > 1 ? (W - pad * 2) / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = pad + i * step;
    const y = H - pad - (d.count / max) * (H - pad * 2);
    return [x, y];
  });
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${(pad + (data.length - 1) * step).toFixed(1)},${H - pad} L${pad},${H - pad} Z`;
  return (
    <div className="admin-sparkline">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H}>
        <defs>
          <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(129,140,248,0.45)" />
            <stop offset="100%" stopColor="rgba(129,140,248,0)" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#sparkFill)" />
        <path d={linePath} fill="none" stroke="#818cf8" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
        {points.map(([x, y], i) => (
          <circle key={data[i].day} cx={x} cy={y} r="2.2" fill="#c7d2fe">
            <title>{data[i].day}: {data[i].count}</title>
          </circle>
        ))}
      </svg>
      <div className="admin-sparkline-labels">
        <span>{data[0]?.day?.slice(5)}</span>
        <span>{data[data.length - 1]?.day?.slice(5)}</span>
      </div>
    </div>
  );
}
